import queryString from 'query-string';
import moment from 'moment';
import lodashSortBy from 'lodash/fp/sortBy';
import lodashIsEmpty from 'lodash/fp/isEmpty';
import lodashPadCharsStart from 'lodash/fp/padCharsStart';
import lodashFind from 'lodash/find';

import typeCheck from 'common/util/typeCheck';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  TOUR_STATUS_REQUESTED,
  TOUR_STATUS_COMPLETED,
  TOUR_STATUS_SCHEDULED,
  TOUR_SHOWING_STATUS_UNREAD,
  TOUR_SHOWING_STATUS_PENDING,
  TOUR_SHOWING_STATUS_CONFIRMED,
  TOUR_SHOWING_STATUS_COMPLETED,
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_AND_SHOWING_CANCEL_REASON_ALL,
  TOUR_SHOWING_RATING_POSITIVE,
  TOUR_SHOWING_RATING_NEGATIVE,
  TOUR_SHOWING_RATING_NO_RESPONSE,
  TOUR_TYPE_RENTER_REQUESTED,
  TOUR_TYPE_RENTER_SCHEDULED,
  TOUR_TYPE_SELF_SHOWING,
  TOUR_TYPE_ADMIN_CREATED,
  TOUR_FEEDBACK_TAGS_LOW_RATING,
  TOUR_FEEDBACK_TAGS_HIGH_RATING,
} from 'common/tourConstants';

import { LISTING_DATA_SOURCE_SUNROOM } from 'common/listingConstants';
import {
  USER_ROLE_LIMITED_TOURS,
  USER_ROLE_ADMIN,
  USER_ROLE_FIELD_OPS,
  USER_ROLE_SELF_SHOWING_TESTER,
} from 'common/userRoles';

const MAX_DAYS = 7;
const MINUTES_TO_SELECT_TIME_SLOT = 45;
const OFFICE_HOURS = {
  Sunday: '10:00-18:00',
  Monday: '11:00-19:00',
  Tuesday: '11:00-19:00',
  Wednesday: '11:00-19:00',
  Thursday: '11:00-19:00',
  Friday: '11:00-19:00',
  Saturday: '10:00-18:00',
};

export const SELF_SCHEDULING_MOMENT_DATE_FORMAT = 'YYYY-MM-DD';

export const SR_USER_GROUP_ROLE_NOT_SHOW_TOUR_FEEDBACK = [
  USER_ROLE_ADMIN,
  USER_ROLE_FIELD_OPS,
  USER_ROLE_SELF_SHOWING_TESTER,
];

export function getTourShowingLabel({ tourShowingStatus, isCompletedTour, hasNote }) {
  switch (tourShowingStatus) {
    case TOUR_SHOWING_STATUS_UNREAD:
    case TOUR_SHOWING_STATUS_PENDING:
      return 'Requested';
    case TOUR_SHOWING_STATUS_CONFIRMED:
      return 'Confirmed';
    case TOUR_SHOWING_STATUS_COMPLETED:
      return 'Completed';
    case TOUR_SHOWING_STATUS_FAILED:
      if (hasNote) {
        return 'Not Available';
      }
      if (isCompletedTour) {
        return 'Didn’t see this home';
      }
      return 'Failed';
  }
}

export function getTourShowingUserRatingLabel(userRating) {
  switch (userRating) {
    case TOUR_SHOWING_RATING_POSITIVE:
      return 'Liked';
    case TOUR_SHOWING_RATING_NEGATIVE:
      return 'Disliked';
    case TOUR_SHOWING_RATING_NO_RESPONSE:
      return 'Skipped';
    default:
      return '--';
  }
}

const formatNumberTwoDigits = lodashPadCharsStart('0')(2);

export function getSelfSchedulingPayload({ date, times }) {
  const selfSchedulingPayload = {};

  if (date) {
    selfSchedulingPayload.startDate = moment(date, SELF_SCHEDULING_MOMENT_DATE_FORMAT)
      .utcOffset(momentWithAustinTimezone().utcOffset(), true)
      .toISOString();
  }

  if (times) {
    selfSchedulingPayload.availableTimes = (Array.isArray(times) ? times : [times])
      .sort((timeA, timeB) => {
        return parseFloat(timeA.replace(':', '.')) - parseFloat(timeB.replace(':', '.'));
      })
      .map((time) => {
        const [hour, minutes] = time.split(':');
        return `${formatNumberTwoDigits(hour)}:${formatNumberTwoDigits(minutes)}`;
      });
  }

  return selfSchedulingPayload;
}

export function hasUserAlreadyScheduledATour({ tours }) {
  return (tours || []).some(({ status }) => (
    [
      TOUR_STATUS_REQUESTED,
      TOUR_STATUS_SCHEDULED,
    ].includes(status)
  ));
}

export function sortToursByStartOrCreatedDate(tours) {
  return lodashSortBy(({ createdDate, startDate }) => (
    (startDate && new Date(startDate).getTime() || 0)
    || (createdDate && new Date(createdDate).getTime() || 0)
    || 0
  ), tours).reverse();
}

export function getOrderedToursWithStatuses(tours, statuses) {
  typeCheck('tours::Maybe Array', tours);
  typeCheck('statuses::Array', statuses);
  return sortToursByStartOrCreatedDate(tours || []).filter(({ status }) => statuses.includes(status));
}

export function getUpcomingTours(tours) {
  return getOrderedToursWithStatuses(tours, [
    TOUR_STATUS_REQUESTED,
    TOUR_STATUS_SCHEDULED,
  ]);
}

export function getUpcomingTour(tours) {
  const [upcomingTour] = getUpcomingTours(tours);
  return upcomingTour;
}

/**
 * Gets the showings in the most recent pending/pendingConfirmation tour.
 *
 * @param  {Array} tours Renter tours list.
 * @return {Array}       Showings in the most recent pending/pendingConfirmation tour.
 */
export function getShowingsInUpcomingTour(tours) {
  const upcomingTour = getUpcomingTour(tours);

  if (!upcomingTour) {
    return [];
  }
  return upcomingTour.listings;
}

export function getApartmentOfficeHours(listing) {
  if (lodashIsEmpty(listing.apartmentOfficeHours)) {
    return {
      Sunday: '',
      Monday: '11:00-17:00',
      Tuesday: '11:00-17:00',
      Wednesday: '11:00-17:00',
      Thursday: '11:00-17:00',
      Friday: '11:00-17:00',
      Saturday: '09:00-17:00',
    };
  }

  return Object.keys(listing.apartmentOfficeHours).reduce((memo, dayOfTheWeek) => {
    memo[dayOfTheWeek] = listing.apartmentOfficeHours[dayOfTheWeek] === 'By Appt'
      ? '09:30-17:30'
      : listing.apartmentOfficeHours[dayOfTheWeek];

    return memo;
  }, {});
}

export function getApartmentOfficeHoursByDate({ listing, date }) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('date::Moment', date);

  const dayOfTheWeek = date.format('dddd');
  const apartmentOfficeHours = getApartmentOfficeHours(listing);
  const apartmentOfficeHoursByDay = apartmentOfficeHours[dayOfTheWeek];

  return apartmentOfficeHoursByDay
    .split('_')
    .filter((hoursString) => !!hoursString)
    .slice(0, 2)
    .map((hoursString) => {
      return hoursString.split('-').map((hourString) => {
        const day = moment(date);
        const [hour, minutes] = hourString.split(':');
        return day.set({ hour: parseInt(hour, 10), minutes: parseInt(minutes, 10) });
      });
    });
}

export function getOfficeHoursByDate({ date }) {
  typeCheck('date::Moment', date);

  const dayOfTheWeek = date.format('dddd');
  const officeHoursByDate = OFFICE_HOURS[dayOfTheWeek];

  const officeHours = officeHoursByDate.split('-').map((hourString) => {
    return moment(
      `${date.format(SELF_SCHEDULING_MOMENT_DATE_FORMAT)} ${hourString}`,
      `${SELF_SCHEDULING_MOMENT_DATE_FORMAT} HH:mm`
    );
  });

  return [officeHours];
}

/**
 * Gets the lapse of range to get available tour dates
 */
export function getAvailableTourDateRange() {
  const startDate = momentWithAustinTimezone();
  const endDate = momentWithAustinTimezone()
    .startOf('day')
    .add(3, 'days');
  return { startDate, endDate };
}

export function getAvailableDates({ availableTourDates, unavailableTourDates, isSunroomListing }) {
  typeCheck('availableTourDates::Array', availableTourDates);

  const dates = [];
  const availableTourDateRange = getAvailableTourDateRange();

  for (let i = 0; i < MAX_DAYS; i += 1) {
    const date = momentWithAustinTimezone();

    if (i > 0) {
      date.add(i, 'days');
    }

    const isDateDisabled = availableTourDateRange.endDate.isAfter(date)
      && !getRangesOfTimes({ date }).some(
        ({ isoStringDate }) => !unavailableTourDates.includes(isoStringDate)
          && (isSunroomListing || availableTourDates.includes(isoStringDate))
      );

    dates.push({
      weekDay: date.weekday(),
      dayNumber: date.date(),
      dayString: (i === 0 ? 'today' : i === 1 ? 'tomorrow' : date.format('dddd')).toLowerCase(),
      month: date.format('MMM').toLowerCase(),
      date: date.format('YYYY-MM-DD'),
      disabled: isDateDisabled,
    });
  }

  return dates;
}

export function getRangesOfTimes({ date }) {
  typeCheck('date::Moment', date);

  const timeSlot = [];
  const openHours = getOfficeHoursByDate({ date });

  if (!openHours.length) {
    return timeSlot;
  }

  const now = moment();
  let startHourDate = openHours[0][0];
  const endHourDate = openHours[openHours.length - 1][1];

  if (startHourDate.isSame(now, 'day')) {
    now.add(MINUTES_TO_SELECT_TIME_SLOT, 'minutes');

    if (now.isAfter(startHourDate)) {
      startHourDate = now.clone();
      startHourDate.add(
        startHourDate.minutes() > 30 ? 60 - startHourDate.minutes() : 30 - startHourDate.minutes(),
        'minutes'
      );
    }
  }

  while (startHourDate.isBefore(endHourDate)) {
    const slot = {
      hour: startHourDate.hour(),
      minute: startHourDate.minute(),
      format: startHourDate.format('HH:mm'),
      isoStringDate: startHourDate.toISOString(),
    };

    timeSlot.push(slot);
    startHourDate.add(30, 'minutes');
  }

  return timeSlot;
}

/**
 * Calculates an approximate time for the duration of a tour.
 * This does not take into account the distance between tours.
 *
 * @param  {Object} tour Tour to calculate the duration for.
 * @return {number}      Approximate number of minutes the tour will take to complete.
 */
export function calculateApproximateTourDurationMinutes(tour) {
  typeCheck('tour::NonEmptyObject', tour);
  return 5 + (tour.listings.length * 15);
}

export function getTourToReschedule({ userTours }) {
  const orderedUserTours = getOrderedToursWithStatuses(userTours, [
    TOUR_STATUS_REQUESTED,
    TOUR_STATUS_SCHEDULED,
  ]);

  const today = moment();
  return orderedUserTours.find((tour) =>
    tour.markedNeedsReschedulingFlag || !tour.startDate || moment(tour.startDate) < today
  );
}

export function isLockedTour({ tour, onUnlocked }) {
  const currentTime = momentWithAustinTimezone();
  const tourStartTime = momentWithAustinTimezone(tour.startDate);
  if (
    tour.status === TOUR_STATUS_SCHEDULED &&
    tour.markedNeedsReschedulingFlag === false &&
    !currentTime.isBefore(tourStartTime.subtract(120, 'minutes'))
  ) {
    global.alert('Please contact the Sunroom Team or your Tour Guide to change your tour. Thank you!');
  }
  else {
    onUnlocked();
  }
}

export function getTourStartDateFormat(startDate) {
  const tourStartDate = momentWithAustinTimezone(startDate);
  const now = momentWithAustinTimezone();
  let dayWeek = tourStartDate.format('dddd');

  if (tourStartDate.isSame(now, 'day')) {
    dayWeek = 'today';
  }
  else if (tourStartDate.isSame(now.add(1, 'day'), 'day')) {
    dayWeek = 'tomorrow';
  }

  return tourStartDate.format(`[${dayWeek}] [at] h:mma`);
}

export function isTourScheduledForToday(tour) {
  return (
    tour.status === TOUR_STATUS_SCHEDULED
    && momentWithAustinTimezone(tour.startDate).isSame(momentWithAustinTimezone(), 'day')
  );
}

export function shouldRenterReviewTour({ currentUser, tours, location, askWantsToApplyToListing }) {
  const expirationDate = momentWithAustinTimezone().subtract(15, 'days');
  const rateTourIdString = queryString.parse(location.search).rateTourId;
  const rateTourId = rateTourIdString ? parseInt(rateTourIdString, 10) : null;
  const rateTour = rateTourId && tours.find((tour) => tour.id === rateTourId);

  if (askWantsToApplyToListing || hasRolesNotShowTourFeedback(currentUser.roles)) {
    return null;
  }

  const tourNeedsUserReview = tours && tours
    .filter((tour) => (
      tour.status === TOUR_STATUS_COMPLETED
      && tour.userId === currentUser.id
      && momentWithAustinTimezone(tour.startDate).isAfter(expirationDate)
      && (
        tour.type === TOUR_TYPE_SELF_SHOWING
          ? momentWithAustinTimezone()
            .isAfter(momentWithAustinTimezone(tour.lastStatusUpdateDate).add(15, 'minutes'))
          : true
      )
    ))
    .find((tour) => {
      const listingWithPendingReview = tour.listings.some((listing) => (
        !listing.tourListingReviewId
        && !listing.tourListingReviewSkipped
        && listing.tourListingStatus === TOUR_SHOWING_STATUS_COMPLETED
      ));

      return listingWithPendingReview;
    });

  return (
    !(location.pathname.includes('tour') && location.pathname.includes('review'))
    && !location.pathname.includes('logout')
    && !location.pathname.includes('login')
    && !location.search.includes('scrollTo=yourQualifications')
    && !location.pathname.includes('application')
    && !location.search.includes('source=unlockListing')
    && !location.search.includes('showSelfShowingSteps')
    && (
      (tourNeedsUserReview && tourNeedsUserReview.id)
      || (rateTour && (!!rateTour.reviewed || !rateTour.reviewSkipped) && rateTour.id)
    )
  );
}

/**
 * Given a tour failure reason constant we return the label.
 *
 * @param  {string} failureReason The constant associated for any tour failure reason.
 * @return {string}               The label associated with a failure reason constant.
 */
export function getTourFailureReasonLabel(failureReason) {
  const labelObject = lodashFind(TOUR_AND_SHOWING_CANCEL_REASON_ALL, ['value', failureReason]);
  return labelObject ? labelObject.label : null;
}

/**
 * Generates the tour dates between two dates.
 *
 * @param  {ISOString} options.startDate Start date.
 * @param  {ISOString} options.endDate End date.
 * @return {Array}
 */
export function makeTourDates({ startDate, endDate }) {
  typeCheck('startDate::DateString', startDate);
  typeCheck('endDate::DateString', endDate);

  const tourDates = [];
  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);

  if (startDateMoment.diff(endDate, 'days') > 8) {
    const error = new Error('Can not calculate tour dates for more than 8 days.');
    error.name = 'SrTourSchedulingMakeTourDatesError';
    throw error;
  }

  const startDateMinute = startDateMoment.minutes();
  if (startDateMinute !== 0 && startDateMinute !== 30) {
    startDateMoment.add(
      startDateMoment.minutes() > 30
        ? 60 - startDateMinute
        : 30 - startDateMinute,
      'minutes'
    );
  }

  while (startDateMoment.isBefore(endDateMoment)) {
    tourDates.push(startDateMoment.toISOString());
    startDateMoment.add(30, 'minutes');
  }

  return tourDates;
}

export function shouldSendTourRequestEmail({ currentUser, listing }) {
  typeCheck('currentUser::NonEmptyObject', currentUser);
  typeCheck('listing::NonEmptyObject', listing);

  return (
    listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM
    && currentUser.roles.includes(USER_ROLE_LIMITED_TOURS)
  );
}

export function shouldAutoAssignTourGuide(tour) {
  return (
    tour.type === TOUR_TYPE_RENTER_REQUESTED
    || tour.type === TOUR_TYPE_RENTER_SCHEDULED
    || tour.type === TOUR_TYPE_ADMIN_CREATED
  );
}

export function getTourFeedbackTags(rating) {
  return rating < 5
    ? TOUR_FEEDBACK_TAGS_LOW_RATING
    : TOUR_FEEDBACK_TAGS_HIGH_RATING;
}

export function hasRolesNotShowTourFeedback(userRoles) {
  return userRoles.some((userRole) => SR_USER_GROUP_ROLE_NOT_SHOW_TOUR_FEEDBACK.includes(userRole));
}
